import { MontoCRC, MontoUSD } from '@/components/dominio';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCuentasBancoBC } from '@/hooks/useCuentasBancoBC';
import { type LineaConCuenta, agruparPorCuenta } from '@/lib/resumenCuentas';
import { cn } from '@/lib/utils';
import type { SaldoCuentaPropuesta } from '@adelante/shared';
import { useMemo } from 'react';

interface Props {
  lineas: LineaConCuenta[];
  saldos: SaldoCuentaPropuesta[];
  onChange: (saldos: SaldoCuentaPropuesta[]) => void;
}

function formatearMonto(monto: number, currencyCode: string | null) {
  return currencyCode === 'USD' ? <MontoUSD monto={monto} /> : <MontoCRC monto={monto} />;
}

/** Panel de saldo disponible por cuenta bancaria de origen usada en la propuesta — el saldo se anota a mano. */
export function DisponibilidadCuentas({ lineas, saldos, onChange }: Props) {
  const { data } = useCuentasBancoBC();
  const cuentas = data?.data ?? [];

  const grupos = useMemo(() => {
    return agruparPorCuenta(lineas, saldos).map((g) => {
      const cuenta = cuentas.find((c) => c.number === g.cuentaBancoOrigen);
      return {
        ...g,
        displayName: cuenta ? `${cuenta.displayName} (${cuenta.number})` : g.cuentaBancoOrigen,
      };
    });
  }, [lineas, cuentas, saldos]);

  function actualizarSaldo(cuentaBancoOrigen: string, texto: string) {
    let saldoDisponible = Number(texto);
    if (!Number.isFinite(saldoDisponible)) saldoDisponible = 0;
    const existe = saldos.some((s) => s.cuentaBancoOrigen === cuentaBancoOrigen);
    onChange(
      existe
        ? saldos.map((s) =>
            s.cuentaBancoOrigen === cuentaBancoOrigen ? { ...s, saldoDisponible } : s,
          )
        : [...saldos, { cuentaBancoOrigen, saldoDisponible }],
    );
  }

  if (grupos.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Asigná una cuenta bancaria de origen a alguna línea para anotar acá el saldo disponible.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Cuenta</TableHead>
          <TableHead>Moneda</TableHead>
          <TableHead className="text-right">Asignado en la propuesta</TableHead>
          <TableHead className="text-right">Saldo disponible</TableHead>
          <TableHead className="text-right">Diferencia</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {grupos.map((g) => (
          <TableRow key={g.cuentaBancoOrigen}>
            <TableCell>{g.displayName}</TableCell>
            <TableCell>{g.currencyCode || 'CRC'}</TableCell>
            <TableCell className="text-right">
              {formatearMonto(g.totalAsignado, g.currencyCode)}
            </TableCell>
            <TableCell className="text-right">
              <Input
                type="number"
                value={g.saldoDisponible}
                step="0.01"
                onChange={(e) => actualizarSaldo(g.cuentaBancoOrigen, e.target.value)}
                className="ml-auto h-8 w-36 text-right"
              />
            </TableCell>
            <TableCell
              className={cn('text-right font-medium', g.diferencia < 0 && 'text-destructive')}
            >
              {formatearMonto(g.diferencia, g.currencyCode)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
